<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { memoryMonitor } from "$lib/utils/memoryMonitor";

  let memoryInfo: any = null;
  let visible = false;
  let unsubscribe: (() => void) | null = null;

  $: isDev = process.env.NODE_ENV === "development";

  onMount(() => {
    if (!isDev) return;

    memoryMonitor.onMemoryUpdate(info => {
      memoryInfo = info;
    });

    // Toggle visibility with Ctrl+M
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "m") {
        e.preventDefault();
        visible = !visible;
        if (visible) {
          memoryInfo = memoryMonitor.getMemoryInfo();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    unsubscribe = () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });

  onDestroy(() => {
    if (unsubscribe) unsubscribe();
  });

  function formatBytes(bytes: number): string {
    const MB = 1024 * 1024;
    return `${(bytes / MB).toFixed(1)}MB`;
  }

  function getUsagePercentage(): number {
    if (!memoryInfo) return 0;
    return (memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit) * 100;
  }

  function getUsageColor(): string {
    const percentage = getUsagePercentage();
    if (percentage > 80) return "#ff4444";
    if (percentage > 60) return "#ffaa00";
    return "#00aa00";
  }
</script>

<style>
  .memory-monitor {
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 12px;
    border-radius: 6px;
    font-family: monospace;
    font-size: 12px;
    z-index: 10000;
    min-width: 200px;
  }

  .memory-bar {
    width: 100%;
    height: 4px;
    background: #333;
    border-radius: 2px;
    overflow: hidden;
    margin: 4px 0;
  }

  .memory-bar-fill {
    height: 100%;
    transition: width 0.3s ease;
  }

  .memory-stats {
    margin-top: 8px;
  }

  .memory-stat {
    display: flex;
    justify-content: space-between;
    margin: 2px 0;
  }

  .hint {
    font-size: 10px;
    opacity: 0.7;
    margin-top: 8px;
  }
</style>

{#if isDev && visible && memoryInfo}
  <div class="memory-monitor">
    <div>Memory Usage: {getUsagePercentage().toFixed(1)}%</div>

    <div class="memory-bar">
      <div
        class="memory-bar-fill"
        style="width: {getUsagePercentage()}%; background-color: {getUsageColor()}"
      ></div>
    </div>

    <div class="memory-stats">
      <div class="memory-stat">
        <span>Used:</span>
        <span>{formatBytes(memoryInfo.usedJSHeapSize)}</span>
      </div>
      <div class="memory-stat">
        <span>Total:</span>
        <span>{formatBytes(memoryInfo.totalJSHeapSize)}</span>
      </div>
      <div class="memory-stat">
        <span>Limit:</span>
        <span>{formatBytes(memoryInfo.jsHeapSizeLimit)}</span>
      </div>
    </div>

    <div class="hint">Press Ctrl+M to toggle</div>
  </div>
{/if}
